/**
 * 1.6.9 (D-1.6.9-3) — Today, at the very top, for the office (administrator, co-administrator, scheduler): one line
 * per closed day that still has people waiting for a new day — "Closed <day> — n to reschedule". Each name is a
 * button that opens Book for that person, exactly as Needs a new day's "Find a day" does (the same follow-up row, so
 * the booking settles it). The line stays until everyone has been rebooked; the server leaves it out for anyone else.
 */
import { useTranslation } from 'react-i18next';
import type { ClosedWaiting } from '@shared/closed';
import { formatDayMedium } from '@/lib/dates';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { useBooking } from '@/features/booking/BookingProvider';

export function ClosedWaitingLine({ days }: { days: ClosedWaiting[] | undefined }) {
  const { t, i18n } = useTranslation();
  const booking = useBooking();
  if (!days?.length) return null;
  return (
    <section className="today-closed" aria-label={t('days169.today.region')} data-testid="today-closed">
      {days.map((d) => (
        <div key={d.dayKey} className="warn today-closed-day" data-testid={`today-closed-${d.dayKey}`}>
          <Icon name="lock" aria-hidden="true" />
          <div>
            <h2 className="today-closed-h">{t('days169.today.title', { day: formatDayMedium(d.dayKey, i18n.language), count: d.people.length })}</h2>
            <p className="help">{t('days169.today.why', { reason: d.reason })}</p>
            <ul className="today-closed-names">
              {d.people.map((p) => (
                <li key={p.followUpId}>
                  {p.inProgress || !booking.canBook ? (
                    <span className="status sched has-icon" data-testid={`today-closed-pending-${p.tenantId}`}>
                      {p.inProgress ? t('days169.today.pending', { name: p.name, unit: p.unit }) : `${p.name} · ${p.unit}`}
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="today-closed-book"
                      aria-label={t('days169.today.book', { name: p.name, unit: p.unit })}
                      data-testid={`today-closed-book-${p.tenantId}`}
                      onClick={(e) => booking.openBook({ followUp: { id: p.followUpId, tenantId: p.tenantId, unit: p.unit, name: p.name, appointment: p.appointment } }, e.currentTarget)}
                    >
                      {p.name} · {p.unit}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </section>
  );
}
